import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Wand2, GitBranch, Inbox, Zap, ArrowRight,
  Check, Mail, Slack, Database, Type, Calendar, Plus,
} from 'lucide-react';

/**
 * HowItWorks — connected 4-step flow with mini product screenshots.
 *
 * 1. Create Your Form
 * 2. Add Logic & Automations
 * 3. Collect Responses
 * 4. Trigger Actions Instantly
 *
 * Steps connect with animated arrows so the eye follows left → right.
 */

// ─── Mini step previews ────────────────────────────────────────────────────

const PreviewCreate = () => (
  <div className="space-y-1.5 text-[10px]">
    <div className="flex items-center justify-between">
      <span className="text-slate-500 font-medium">My new form</span>
      <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">
        <Plus className="w-2.5 h-2.5" /> Add field
      </span>
    </div>
    {[
      { Icon: Type,     label: 'Full name' },
      { Icon: Mail,     label: 'Email address' },
      { Icon: Calendar, label: 'Preferred date' },
    ].map(({ Icon, label }, i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, x: -6 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.1 + i * 0.1 }}
        className="flex items-center gap-2 bg-white rounded border border-slate-200 px-2 py-1.5"
      >
        <Icon className="w-3 h-3 text-indigo-500" />
        <span className="text-slate-700 font-medium flex-1">{label}</span>
      </motion.div>
    ))}
  </div>
);

const PreviewLogic = () => (
  <div className="space-y-1.5 text-[10px]">
    <div className="flex items-center gap-1 flex-wrap">
      <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-mono font-bold">IF</span>
      <span className="text-slate-600">Budget</span>
      <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-mono">{'>'}</span>
      <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-bold">$10k</span>
    </div>
    <div className="ml-2 pl-2 border-l-2 border-emerald-200 space-y-1.5">
      <div className="flex items-center gap-1">
        <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-mono font-bold">THEN</span>
        <span className="text-slate-600 truncate">Route to Sales</span>
      </div>
      <div className="flex items-center gap-1">
        <Slack className="w-3 h-3 text-emerald-600" />
        <span className="text-slate-600 truncate">Notify #priority</span>
      </div>
    </div>
  </div>
);

const PreviewCollect = () => {
  const rows = [
    { name: 'Aisha Patel',   form: 'Lead form',     time: 'now',    dot: 'bg-emerald-500 animate-pulse' },
    { name: 'Marcus Reid',   form: 'Onboarding',    time: '2m',     dot: 'bg-indigo-500' },
    { name: 'Lin Chen',      form: 'RSVP',          time: '8m',     dot: 'bg-violet-500' },
  ];
  return (
    <div className="space-y-1.5 text-[10px]">
      <div className="flex items-center justify-between">
        <span className="font-medium text-slate-600">Inbox</span>
        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-bold">
          {rows.length} new
        </span>
      </div>
      {rows.map((r, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 4 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.15 + i * 0.12 }}
          className="flex items-center gap-2 bg-white rounded border border-slate-200 px-2 py-1.5"
        >
          <span className={`w-1.5 h-1.5 rounded-full ${r.dot} shrink-0`} />
          <span className="text-slate-700 font-medium truncate flex-1">{r.name}</span>
          <span className="text-slate-400">{r.time}</span>
        </motion.div>
      ))}
    </div>
  );
};

const PreviewTrigger = () => {
  const actions = [
    { Icon: Mail,     label: 'Send welcome email', color: 'bg-rose-100 text-rose-600',      done: true  },
    { Icon: Slack,    label: 'Ping #sales channel', color: 'bg-emerald-100 text-emerald-600', done: true  },
    { Icon: Database, label: 'Push row to CRM',    color: 'bg-violet-100 text-violet-600',  done: false },
  ];
  return (
    <div className="space-y-1.5 text-[10px]">
      {actions.map((a, i) => {
        const { Icon, label, color, done } = a;
        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -6 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 + i * 0.12 }}
            className="flex items-center gap-2 bg-white rounded border border-slate-200 px-2 py-1.5"
          >
            <span className={`w-5 h-5 rounded ${color} flex items-center justify-center shrink-0`}>
              <Icon className="w-3 h-3" />
            </span>
            <span className="text-slate-700 font-medium flex-1 truncate">{label}</span>
            {done ? (
              <Check className="w-3 h-3 text-emerald-500" strokeWidth={3} />
            ) : (
              <span className="w-3 h-3 rounded-full border-2 border-slate-300 border-t-indigo-500 animate-spin" />
            )}
          </motion.div>
        );
      })}
    </div>
  );
};

// ─── Steps config ──────────────────────────────────────────────────────────

const STEPS = [
  {
    n: '01',
    Icon: Wand2,
    title: 'Create Your Form',
    desc: 'Drag, drop, customize. From contact forms to multi-step intakes — done in minutes.',
    accent: 'from-indigo-500 to-violet-500',
    Preview: PreviewCreate,
  },
  {
    n: '02',
    Icon: GitBranch,
    title: 'Add Logic & Automations',
    desc: 'Conditional fields, smart routing, approvals — all without a line of code.',
    accent: 'from-amber-500 to-orange-500',
    Preview: PreviewLogic,
  },
  {
    n: '03',
    Icon: Inbox,
    title: 'Collect Responses',
    desc: 'Submissions land in a centralized inbox with audit logs and exports.',
    accent: 'from-emerald-500 to-teal-500',
    Preview: PreviewCollect,
  },
  {
    n: '04',
    Icon: Zap,
    title: 'Trigger Actions Instantly',
    desc: 'Emails, Slack pings, CRM rows, webhooks — fired the moment a form is submitted.',
    accent: 'from-rose-500 to-pink-500',
    Preview: PreviewTrigger,
  },
];

// ─── Section component ────────────────────────────────────────────────────

const SPOTLIGHT_INTERVAL = 2400; // ms between step changes

export const HowItWorks = () => {
  const [active, setActive] = useState(0);

  // Cycle the "spotlight" through all 4 cards in a loop
  useEffect(() => {
    const id = setInterval(() => {
      setActive((i) => (i + 1) % STEPS.length);
    }, SPOTLIGHT_INTERVAL);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      className="py-20 lg:py-24 bg-gradient-to-b from-white to-slate-50 overflow-hidden relative"
      data-testid="how-it-works-section"
    >
      {/* Soft moving glow background that follows the active step */}
      <motion.div
        aria-hidden
        animate={{
          left: `${12.5 + active * 25}%`,
        }}
        transition={{ duration: 0.8, ease: 'easeInOut' }}
        className="hidden lg:block absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] h-[420px] rounded-full pointer-events-none"
        style={{
          background:
            'radial-gradient(circle, rgba(99,102,241,0.18) 0%, rgba(139,92,246,0.08) 40%, transparent 70%)',
          filter: 'blur(20px)',
        }}
      />

      <div className="max-w-7xl mx-auto px-6 relative">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.4 }}
          className="text-center max-w-3xl mx-auto mb-12 lg:mb-16"
        >
          <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-indigo-600 mb-3">
            How it works
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
            From idea to{' '}
            <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
              automated workflow
            </span>{' '}
            in 4 steps
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
            No engineering. No glue scripts. Just a faster way to turn forms into outcomes.
          </p>
        </motion.div>

        {/* Steps with connecting arrows */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-3 xl:gap-5 relative">
          {STEPS.map((s, i) => {
            const { n, Icon, title, desc, accent, Preview } = s;
            const isLast = i === STEPS.length - 1;
            const isActive = i === active;
            return (
              <React.Fragment key={n}>
                <motion.div
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.45, delay: 0.05 + i * 0.12 }}
                  whileHover={{ y: -6 }}
                  animate={{
                    y: isActive ? -8 : 0,
                    scale: isActive ? 1.02 : 1,
                  }}
                  className={`relative rounded-2xl border bg-white p-5 transition-colors duration-500 overflow-hidden ${
                    isActive
                      ? 'border-indigo-300 shadow-[0_20px_50px_-15px_rgba(99,102,241,0.4)]'
                      : 'border-slate-200 shadow-sm hover:shadow-xl hover:border-slate-300'
                  }`}
                  data-testid={`how-step-${n}`}
                >
                  {/* gradient accent strip */}
                  <span
                    className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${accent} transition-opacity duration-500 ${
                      isActive ? 'opacity-100' : 'opacity-60'
                    }`}
                    aria-hidden
                  />

                  {/* Animated step number watermark */}
                  <motion.span
                    animate={{
                      scale: isActive ? 1.08 : 1,
                      opacity: isActive ? 0.18 : 0.08,
                    }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                    className={`absolute -top-2 -right-2 text-7xl font-black bg-gradient-to-br ${accent} bg-clip-text text-transparent select-none leading-none`}
                  >
                    {n}
                  </motion.span>

                  {/* Icon + step pill (icon bounces + has a rotating ring when active) */}
                  <div className="relative flex items-center gap-2 mb-3">
                    <motion.span
                      animate={{
                        scale: isActive ? [1, 1.12, 1] : 1,
                        rotate: isActive ? [0, -6, 6, 0] : 0,
                      }}
                      transition={{
                        duration: isActive ? 0.8 : 0.3,
                        ease: 'easeInOut',
                      }}
                      className={`shrink-0 relative inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br ${accent} text-white shadow-md`}
                    >
                      <Icon className="w-5 h-5" strokeWidth={2.2} />

                      {/* Rotating glow ring around the icon when active */}
                      {isActive && (
                        <motion.span
                          aria-hidden
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1.3, rotate: 360 }}
                          transition={{
                            opacity: { duration: 0.3 },
                            scale: { duration: 0.5 },
                            rotate: { duration: 4, repeat: Infinity, ease: 'linear' },
                          }}
                          exit={{ opacity: 0 }}
                          className={`absolute inset-0 rounded-xl bg-gradient-to-br ${accent} blur-md -z-10`}
                        />
                      )}
                    </motion.span>
                    <span className="text-[11px] font-bold tracking-widest uppercase text-slate-400">
                      Step {n}
                    </span>

                    {/* Live pulse dot on the active card */}
                    {isActive && (
                      <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold tracking-wide uppercase text-emerald-600"
                      >
                        <span className="relative inline-flex w-1.5 h-1.5">
                          <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping" />
                          <span className="relative w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        </span>
                        Live
                      </motion.span>
                    )}
                  </div>

                  <h3 className="relative text-base font-semibold text-slate-900 leading-tight mb-2">
                    {title}
                  </h3>
                  <p className="relative text-sm text-slate-600 leading-relaxed mb-4">
                    {desc}
                  </p>

                  {/* Mini screenshot */}
                  <div className="relative rounded-xl bg-slate-50 border border-slate-100 p-3">
                    <Preview />
                  </div>
                </motion.div>

                {/* Connecting arrow — desktop horizontal between cards */}
                {!isLast && (
                  <div
                    className="hidden lg:flex absolute top-1/2 -translate-y-1/2 z-10 items-center justify-center pointer-events-none"
                    style={{ left: `calc(${(i + 1) * 25}% - 14px)` }}
                  >
                    <motion.div
                      animate={{
                        scale: i === active ? [1, 1.25, 1] : 1,
                        boxShadow: i === active
                          ? '0 0 0 6px rgba(99,102,241,0.18)'
                          : '0 0 0 0px rgba(99,102,241,0)',
                      }}
                      transition={{ duration: 0.6 }}
                      className="relative bg-white rounded-full p-1.5 ring-1 ring-slate-200 shadow-sm"
                    >
                      <motion.div
                        animate={i === active ? { x: [0, 3, 0] } : { x: 0 }}
                        transition={{ duration: 1, repeat: i === active ? Infinity : 0, ease: 'easeInOut' }}
                      >
                        <ArrowRight className="w-4 h-4 text-indigo-500" strokeWidth={2.4} />
                      </motion.div>
                    </motion.div>
                  </div>
                )}
                {/* Connecting arrow — mobile vertical */}
                {!isLast && (
                  <div className="lg:hidden flex justify-center -my-2">
                    <motion.div
                      animate={{
                        y: i === active ? [0, 4, 0] : 0,
                        boxShadow: i === active
                          ? '0 0 0 6px rgba(99,102,241,0.18)'
                          : '0 0 0 0px rgba(99,102,241,0)',
                      }}
                      transition={{ duration: 1, repeat: i === active ? Infinity : 0 }}
                      className="bg-white rounded-full p-1.5 ring-1 ring-slate-200 shadow-sm rotate-90"
                    >
                      <ArrowRight className="w-4 h-4 text-indigo-500" strokeWidth={2.4} />
                    </motion.div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step indicator dots — clickable to jump to a step */}
        <div className="mt-10 flex items-center justify-center gap-2" role="tablist" aria-label="Workflow steps">
          {STEPS.map((s, i) => (
            <button
              key={s.n}
              type="button"
              role="tab"
              aria-selected={i === active}
              aria-label={`Go to ${s.title}`}
              onClick={() => setActive(i)}
              data-testid={`how-it-works-pill-${s.n}`}
              className="group flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
              style={{
                backgroundColor: i === active ? '#0f172a' : 'transparent',
                color: i === active ? 'white' : '#475569',
              }}
            >
              <motion.span
                animate={{
                  scale: i === active ? 1.2 : 1,
                }}
                className={`w-1.5 h-1.5 rounded-full ${i === active ? 'bg-emerald-400' : 'bg-slate-300'}`}
              />
              {s.title}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
